/**
 * Centralized auth + CSRF aware fetch helpers for admin components.
 *
 * Replaces 9 near-duplicate `getAuthHeaders()` definitions across admin
 * components. Reads the in-memory auth token (set by AuthContext) and the
 * CSRF cookie. Sends `credentials: 'include'` so the httpOnly auth cookie
 * is automatically attached as well.
 *
 * Usage:
 *   const { get, post, headers } = useAdminFetch();
 *   const data = await get('/api/admin/courses');
 *   await post('/api/admin/foo', { name: 'bar' });
 *
 * `headers(json=true)` returns a header object for legacy code paths that
 * still call `fetch()` directly (e.g. multipart uploads, EventSource etc.).
 */
import { useCallback, useMemo } from 'react';
import { getAuthToken, getCsrfToken } from '@/lib/apiClient';

const buildHeaders = (json = true) => {
  const h = {};
  const token = getAuthToken();
  if (token) h.Authorization = `Bearer ${token}`;
  const csrf = getCsrfToken();
  if (csrf) h['X-CSRF-Token'] = csrf;
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const parseResponse = async (response) => {
  if (response.status === 204) return null;
  const ct = response.headers.get('content-type') || '';
  if (ct.includes('application/json')) return response.json();
  return response.text();
};

const request = async (url, { method = 'GET', body, signal, raw = false, headers: extra } = {}) => {
  const init = {
    method,
    credentials: 'include',
    headers: { ...buildHeaders(typeof body === 'object' && body !== null && !(body instanceof FormData)), ...(extra || {}) },
    signal,
  };
  if (body !== undefined) {
    init.body = body instanceof FormData || typeof body === 'string'
      ? body
      : JSON.stringify(body);
  }
  // FormData must not have a Content-Type header (browser sets boundary)
  if (body instanceof FormData) {
    delete init.headers['Content-Type'];
  }

  const response = await fetch(url, init);
  if (raw) return response;

  if (!response.ok) {
    const errBody = await parseResponse(response).catch(() => null);
    const detail = (errBody && (errBody.detail || errBody.error || errBody.message)) || response.statusText;
    const error = new Error(detail || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = errBody;
    throw error;
  }

  return parseResponse(response);
};

const useAdminFetch = () => {
  const get = useCallback((url, opts) => request(url, { ...opts, method: 'GET' }), []);
  const post = useCallback((url, body, opts) => request(url, { ...opts, method: 'POST', body }), []);
  const put = useCallback((url, body, opts) => request(url, { ...opts, method: 'PUT', body }), []);
  const patch = useCallback((url, body, opts) => request(url, { ...opts, method: 'PATCH', body }), []);
  const del = useCallback((url, opts) => request(url, { ...opts, method: 'DELETE' }), []);
  const headers = useCallback((json = true) => buildHeaders(json), []);

  return useMemo(
    () => ({ get, post, put, patch, del, headers, request }),
    [get, post, put, patch, del, headers],
  );
};

// Module-level helpers for non-component code paths
export const adminFetchHeaders = buildHeaders;
export const adminFetch = request;

export default useAdminFetch;
